import { useRef, useLayoutEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import { OrthographicCamera } from '@react-three/drei';
import type { OrthographicCamera as OrthographicCameraType } from 'three';
import * as THREE from 'three';
import { CAMERA } from '../constants';

interface IsometricCameraProps {
  zoom?: number;
  target?: [number, number, number];
  followSpeed?: number;
}

export function IsometricCamera({
  zoom = CAMERA.orthographic.zoom,
  target = [0, 0, 0],
  followSpeed = 5,
}: IsometricCameraProps) {
  const cameraRef = useRef<OrthographicCameraType>(null);
  const currentTarget = useRef(new THREE.Vector3(...target));
  const desiredTarget = useRef(new THREE.Vector3(...target));
  const currentPosition = useRef(new THREE.Vector3());

  // Isometric camera offset calculations
  const distance = CAMERA.distance;
  const angle = Math.PI / 4; // 45 degrees
  const elevation = CAMERA.isometricAngle;

  // Calculate offset from target to camera position
  const offsetX = distance * Math.sin(angle) * Math.cos(elevation);
  const offsetY = distance * Math.sin(elevation);
  const offsetZ = distance * Math.cos(angle) * Math.cos(elevation);

  // Initialize position
  useLayoutEffect(() => {
    currentTarget.current.set(target[0], target[1], target[2]);
    desiredTarget.current.set(target[0], target[1], target[2]);
    currentPosition.current.set(
      target[0] + offsetX,
      target[1] + offsetY,
      target[2] + offsetZ
    );
  }, []);

  // Update desired target when target prop changes
  useLayoutEffect(() => {
    desiredTarget.current.set(target[0], target[1], target[2]);
  }, [target[0], target[1], target[2]]);

  // Smoothly follow target each frame
  useFrame((_, delta) => {
    if (cameraRef.current) {
      // Smoothly interpolate current target towards desired target
      const lerpFactor = 1 - Math.exp(-followSpeed * delta);
      currentTarget.current.lerp(desiredTarget.current, lerpFactor);

      // Update camera position based on current target
      currentPosition.current.set(
        currentTarget.current.x + offsetX,
        currentTarget.current.y + offsetY,
        currentTarget.current.z + offsetZ
      );

      // Apply position and look at target
      cameraRef.current.position.copy(currentPosition.current);
      cameraRef.current.lookAt(currentTarget.current);
    }
  });

  // Initial position
  const initialX = target[0] + offsetX;
  const initialY = target[1] + offsetY;
  const initialZ = target[2] + offsetZ;

  return (
    <OrthographicCamera
      ref={cameraRef}
      makeDefault
      position={[initialX, initialY, initialZ]}
      zoom={zoom}
      near={CAMERA.near}
      far={CAMERA.far}
    />
  );
}
